import React, { useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import JSZip from "jszip";
import { toast } from "sonner";
import {
  FileSpreadsheet,
  FileArchive,
  Download,
  Printer,
  Calendar,
  CheckCircle2,
  AlertCircle,
  IndianRupee,
  Package,
  ExternalLink,
  FileText,
  Loader2,
  Info,
  Layers,
  Sparkles,
} from "lucide-react";
import { COMPANY, isGstConfigured } from "../lib/company";
import { getInvoiceNo, isWestBengalState } from "../lib/orderHelpers";
import InvoiceSheet from "./InvoiceSheet";
import {
  MONTH_NAMES,
  filterOrdersByMonthYear,
  getOrderTaxBreakdown,
  exportGstSalesExcel,
  captureInvoiceElementToPdfBlob,
} from "../lib/gstExportHelpers";

export default function GstSalesPanel({ orders = [], products = [], refresh }) {
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth());
  const [statusFilter, setStatusFilter] = useState("non-cancelled");
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [isExportingZip, setIsExportingZip] = useState(false);
  const [zipProgress, setZipProgress] = useState(null);
  const [activeCaptureOrder, setActiveCaptureOrder] = useState(null);
  const [singlePdfLoadingId, setSinglePdfLoadingId] = useState(null);

  const captureRef = useRef(null);

  const hasGST = isGstConfigured();

  // Extract all available years from orders + current year
  const availableYears = useMemo(() => {
    const yearsSet = new Set([currentDate.getFullYear(), 2024, 2025, 2026]);
    orders.forEach((o) => {
      const raw = o.createdAt?.seconds ? o.createdAt.seconds * 1000 : o.createdAt;
      if (raw) {
        const y = new Date(raw).getFullYear();
        if (!isNaN(y)) yearsSet.add(y);
      }
    });
    return Array.from(yearsSet).sort((a, b) => b - a);
  }, [orders, currentDate]);

  // Filter orders for selected month, year and status EXCLUDING Non-GST orders
  const { monthlyOrders, nonGstCount } = useMemo(() => {
    const byMonth = filterOrdersByMonthYear(orders, selectedMonth, selectedYear);
    let excludedNonGst = 0;
    const filtered = byMonth.filter((o) => {
      const isNonGst =
        o.isNonGst === true ||
        o.nonGst === true ||
        o.isGstOrder === false ||
        o.gstType === "non-gst" ||
        o.gstMode === "none" ||
        o.gstMode === "non-gst";

      if (isNonGst) {
        excludedNonGst++;
        return false;
      }
      const st = (o.status || "").toLowerCase();
      if (statusFilter === "non-cancelled") return st !== "cancelled";
      if (statusFilter === "completed") return st === "completed" || st === "delivered";
      return true; // "all"
    });
    return { monthlyOrders: filtered, nonGstCount: excludedNonGst };
  }, [orders, selectedMonth, selectedYear, statusFilter]);

  // Aggregate monthly stats
  const monthlyStats = useMemo(() => {
    let totalQty = 0;
    let totalTaxable = 0;
    let totalTax = 0;
    let totalCGST = 0;
    let totalSGST = 0;
    let totalIGST = 0;
    let totalShipping = 0;
    let totalGross = 0;

    monthlyOrders.forEach((o) => {
      const breakdown = getOrderTaxBreakdown(o, products, COMPANY);
      totalQty += (o.items || []).reduce((s, i) => s + Number(i.quantity || 1), 0);
      totalTaxable += breakdown.taxableValue;
      totalTax += breakdown.totalTaxAmount;
      totalCGST += breakdown.cgst;
      totalSGST += breakdown.sgst;
      totalIGST += breakdown.igst;
      totalShipping += breakdown.invoiceShipping;
      totalGross += breakdown.grandTotal;
    });

    return {
      orderCount: monthlyOrders.length,
      totalQty,
      totalTaxable,
      totalTax,
      totalCGST,
      totalSGST,
      totalIGST,
      totalShipping,
      totalGross,
    };
  }, [monthlyOrders, products]);

  // Export Excel handler
  const handleExportExcel = async () => {
    if (monthlyOrders.length === 0) {
      toast.error(`No orders found for ${MONTH_NAMES[selectedMonth]} ${selectedYear}`);
      return;
    }
    setIsExportingExcel(true);
    try {
      const fileName = exportGstSalesExcel(
        monthlyOrders,
        products,
        selectedMonth,
        selectedYear,
        COMPANY
      );
      toast.success(`Downloaded ${fileName} successfully!`);
    } catch (err) {
      console.error("Excel export error:", err);
      toast.error("Failed to generate Excel file. Please try again.");
    } finally {
      setIsExportingExcel(false);
    }
  };

  // Export Invoices ZIP handler
  const handleExportZip = async () => {
    if (monthlyOrders.length === 0) {
      toast.error(`No orders found for ${MONTH_NAMES[selectedMonth]} ${selectedYear}`);
      return;
    }

    setIsExportingZip(true);
    setZipProgress({ current: 0, total: monthlyOrders.length, currentInvoice: "Preparing..." });

    try {
      const zip = new JSZip();
      const monthName = MONTH_NAMES[selectedMonth];

      for (let i = 0; i < monthlyOrders.length; i++) {
        const order = monthlyOrders[i];
        const invoiceNo = getInvoiceNo(order);
        const cleanName = (order.customerName || "Customer").replace(/[^a-zA-Z0-9_-]/g, "_");

        setZipProgress({
          current: i + 1,
          total: monthlyOrders.length,
          currentInvoice: `#${invoiceNo} (${order.customerName || "Customer"})`,
        });

        // Set active order for hidden DOM rendering
        setActiveCaptureOrder(order);

        // Wait for DOM to render the invoice
        await new Promise((r) => setTimeout(r, 90));

        if (captureRef.current) {
          const pdfBlob = await captureInvoiceElementToPdfBlob(captureRef.current);
          const pdfFileName = `Invoice_${invoiceNo}_${cleanName}.pdf`;
          zip.file(pdfFileName, pdfBlob);
        }
      }

      setZipProgress({
        current: monthlyOrders.length,
        total: monthlyOrders.length,
        currentInvoice: "Compressing into ZIP archive...",
      });

      const zipBlob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 },
      });

      // Trigger download
      const downloadUrl = URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `Invoices_${monthName}_${selectedYear}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);

      toast.success(
        `Downloaded all ${monthlyOrders.length} invoices as ZIP for ${monthName} ${selectedYear}!`
      );
    } catch (err) {
      console.error("ZIP export error:", err);
      toast.error("Failed to generate invoices ZIP. Please try again.");
    } finally {
      setIsExportingZip(false);
      setZipProgress(null);
      setActiveCaptureOrder(null);
    }
  };

  // Download single PDF directly from table
  const handleDownloadSinglePdf = async (order) => {
    setSinglePdfLoadingId(order.id);
    try {
      setActiveCaptureOrder(order);
      await new Promise((r) => setTimeout(r, 90));

      if (captureRef.current) {
        const pdfBlob = await captureInvoiceElementToPdfBlob(captureRef.current);
        const invoiceNo = getInvoiceNo(order);
        const cleanName = (order.customerName || "Customer").replace(/[^a-zA-Z0-9_-]/g, "_");

        const downloadUrl = URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `Invoice_${invoiceNo}_${cleanName}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);

        toast.success(`Downloaded Invoice #${invoiceNo}`);
      }
    } catch (err) {
      console.error("Single PDF download error:", err);
      toast.error("Failed to download PDF invoice.");
    } finally {
      setSinglePdfLoadingId(null);
      setActiveCaptureOrder(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* ─── Top Context Header & GST Mode Indicator ─── */}
      <div className="bg-card border border-border rounded-xl p-6 shadow-xs">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="p-2 rounded-lg bg-terracotta/10 text-terracotta">
                <FileSpreadsheet className="w-5 h-5" />
              </span>
              <div>
                <h2 className="text-xl font-serif font-bold text-foreground">
                  Monthly GST Sales &amp; Invoices
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Download monthly sales register as Excel and batch-export all customer invoices as PDF inside a ZIP file.
                </p>
              </div>
            </div>
          </div>

          {/* GST Configuration Status Pill */}
          <div className="flex items-center gap-2 self-start md:self-auto">
            {hasGST ? (
              <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 dark:text-emerald-400 px-3 py-1.5 rounded-full text-xs font-medium">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span>
                  GST Active: <strong>{COMPANY.gst}</strong> (Tax Invoice Mode)
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 text-amber-800 dark:text-amber-300 px-3 py-1.5 rounded-full text-xs font-medium">
                <Info className="w-4 h-4 text-amber-600" />
                <span>
                  Standard Bill Mode (No GSTIN in ENV · Invoices omit tax breakdown)
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Filter Controls Bar */}
        <div className="mt-6 pt-5 border-t border-border flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Month Select */}
            <div className="flex items-center gap-2 bg-muted/60 px-3 py-1.5 rounded-lg border border-border">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <label htmlFor="gst-month-select" className="text-xs font-medium text-muted-foreground">Month:</label>
              <select
                id="gst-month-select"
                data-testid="gst-month-select"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="bg-transparent text-sm font-semibold text-foreground focus:outline-hidden cursor-pointer"
              >
                {MONTH_NAMES.map((m, idx) => (
                  <option key={m} value={idx}>
                    {m}
                  </option>
                ))}
              </select>
            </div>

            {/* Year Select */}
            <div className="flex items-center gap-2 bg-muted/60 px-3 py-1.5 rounded-lg border border-border">
              <label htmlFor="gst-year-select" className="text-xs font-medium text-muted-foreground">Year:</label>
              <select
                id="gst-year-select"
                data-testid="gst-year-select"
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="bg-transparent text-sm font-semibold text-foreground focus:outline-hidden cursor-pointer"
              >
                {availableYears.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            {/* Order Status Select */}
            <div className="flex items-center gap-2 bg-muted/60 px-3 py-1.5 rounded-lg border border-border">
              <label htmlFor="gst-status-filter" className="text-xs font-medium text-muted-foreground">Filter:</label>
              <select
                id="gst-status-filter"
                data-testid="gst-status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-transparent text-xs font-medium text-foreground focus:outline-hidden cursor-pointer"
              >
                <option value="non-cancelled">Valid Sales (Non-Cancelled)</option>
                <option value="completed">Delivered / Completed Only</option>
                <option value="all">All Orders (Including Cancelled)</option>
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            {/* Download Excel Button */}
            <button
              data-testid="download-gst-excel-btn"
              onClick={handleExportExcel}
              disabled={isExportingExcel || isExportingZip || monthlyOrders.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white text-xs font-medium shadow-xs transition"
              title="Download Monthly GST Sales Register (.xlsx)"
            >
              {isExportingExcel ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-4 h-4" />
              )}
              <span>Download Sales Excel (.xlsx)</span>
            </button>

            {/* Download ZIP Button */}
            <button
              data-testid="download-invoices-zip-btn"
              onClick={handleExportZip}
              disabled={isExportingZip || isExportingExcel || monthlyOrders.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-terracotta hover:opacity-90 disabled:opacity-50 text-white text-xs font-medium shadow-xs transition"
              title="Download all monthly invoices packaged as PDF inside a ZIP archive"
            >
              {isExportingZip ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileArchive className="w-4 h-4" />
              )}
              <span>Download Invoices (ZIP)</span>
            </button>
          </div>
        </div>

        {/* Live ZIP Generation Progress Bar */}
        {isExportingZip && zipProgress && (
          <div className="mt-4 p-4 rounded-lg bg-terracotta/5 border border-terracotta/20 animate-in fade-in duration-200">
            <div className="flex items-center justify-between text-xs mb-1.5 font-medium">
              <span className="flex items-center gap-2 text-terracotta">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Packaging Invoices into ZIP: {zipProgress.current} of {zipProgress.total}
              </span>
              <span className="text-muted-foreground truncate max-w-xs font-mono">
                {zipProgress.currentInvoice}
              </span>
            </div>
            <div className="w-full bg-border rounded-full h-2 overflow-hidden">
              <div
                className="bg-terracotta h-2 transition-all duration-150"
                style={{
                  width: `${Math.round((zipProgress.current / zipProgress.total) * 100)}%`,
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* ─── Monthly Summary Metric Cards ─── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-card border border-border p-4 rounded-xl shadow-2xs">
          <div className="text-[11px] font-medium text-muted-foreground">Invoices / Orders</div>
          <div className="text-xl font-bold font-price mt-1 text-foreground">
            {monthlyStats.orderCount}
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            {MONTH_NAMES[selectedMonth]} {selectedYear}
          </div>
        </div>

        <div className="bg-card border border-border p-4 rounded-xl shadow-2xs">
          <div className="text-[11px] font-medium text-muted-foreground">Total Bags / Units</div>
          <div className="text-xl font-bold font-price mt-1 text-foreground">
            {monthlyStats.totalQty}{" "}
            <span className="text-xs font-normal text-muted-foreground">Pcs</span>
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">Shipped products</div>
        </div>

        <div className="bg-card border border-border p-4 rounded-xl shadow-2xs">
          <div className="text-[11px] font-medium text-muted-foreground">Taxable Value</div>
          <div className="text-xl font-bold font-price mt-1 text-foreground">
            ₹{Math.round(monthlyStats.totalTaxable).toLocaleString("en-IN")}
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">Subtotal excl. tax</div>
        </div>

        <div className="bg-card border border-border p-4 rounded-xl shadow-2xs">
          <div className="text-[11px] font-medium text-muted-foreground">Total Tax (GST)</div>
          <div className="text-xl font-bold font-price mt-1 text-foreground">
            ₹{Math.round(monthlyStats.totalTax).toLocaleString("en-IN")}
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            {hasGST
              ? `CGST: ₹${Math.round(monthlyStats.totalCGST)} · SGST: ₹${Math.round(monthlyStats.totalSGST)} · IGST: ₹${Math.round(monthlyStats.totalIGST)}`
              : "0% (Bill mode)"}
          </div>
        </div>

        <div className="bg-card border border-border p-4 rounded-xl shadow-2xs">
          <div className="text-[11px] font-medium text-muted-foreground">Shipping Billed</div>
          <div className="text-xl font-bold font-price mt-1 text-foreground">
            ₹{Math.round(monthlyStats.totalShipping).toLocaleString("en-IN")}
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">Customer freight</div>
        </div>

        <div className="bg-card border border-border p-4 rounded-xl shadow-2xs bg-terracotta/5 border-terracotta/20">
          <div className="text-[11px] font-medium text-terracotta">Gross Invoiced</div>
          <div className="text-xl font-bold font-price mt-1 text-terracotta">
            ₹{Math.round(monthlyStats.totalGross).toLocaleString("en-IN")}
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">Grand Total sales</div>
        </div>
      </div>

      {/* ─── Orders & Invoices Table ─── */}
      <div className="bg-card border border-border rounded-xl shadow-xs overflow-hidden">
        <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm">
              Invoices for {MONTH_NAMES[selectedMonth]} {selectedYear}
            </span>
            <span className="text-xs text-muted-foreground">
              ({monthlyOrders.length} {monthlyOrders.length === 1 ? "record" : "records"})
            </span>
            {nonGstCount > 0 && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-800 dark:text-amber-300 border border-amber-500/20 font-medium">
                {nonGstCount} Non-GST order{nonGstCount > 1 ? "s" : ""} excluded
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            Click &ldquo;View / Print&rdquo; or &ldquo;Download PDF&rdquo; to access single invoices.
          </div>
        </div>

        {monthlyOrders.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <Calendar className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
            <h3 className="font-serif text-lg font-medium text-foreground">
              No sales records found
            </h3>
            <p className="text-xs mt-1">
              There are no orders registered for {MONTH_NAMES[selectedMonth]} {selectedYear}. Try
              selecting another month or year above.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left border-collapse">
              <thead>
                <tr className="bg-muted/60 border-b border-border font-semibold text-muted-foreground">
                  <th className="py-2.5 px-3">Invoice #</th>
                  <th className="py-2.5 px-3">Date</th>
                  <th className="py-2.5 px-3">Customer</th>
                  <th className="py-2.5 px-3">State / Destination</th>
                  <th className="py-2.5 px-3">Items</th>
                  <th className="py-2.5 px-3 text-right">Taxable</th>
                  {hasGST && <th className="py-2.5 px-3 text-right">Tax Amt</th>}
                  <th className="py-2.5 px-3 text-right">Grand Total</th>
                  <th className="py-2.5 px-3">Status</th>
                  <th className="py-2.5 px-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {monthlyOrders.map((o) => {
                  const created = new Date(
                    o.createdAt?.seconds ? o.createdAt.seconds * 1000 : o.createdAt
                  );
                  const invoiceNo = getInvoiceNo(o);
                  const breakdown = getOrderTaxBreakdown(o, products, COMPANY);

                  return (
                    <tr key={o.id} className="hover:bg-muted/40 transition">
                      {/* Invoice No */}
                      <td className="py-2.5 px-3 font-mono font-bold text-foreground">
                        <Link
                          to={`/admin/print/${o.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-terracotta hover:underline inline-flex items-center gap-1"
                        >
                          #{invoiceNo}
                          <ExternalLink className="w-3 h-3 opacity-60" />
                        </Link>
                      </td>

                      {/* Date */}
                      <td className="py-2.5 px-3 text-muted-foreground whitespace-nowrap">
                        {created.toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </td>

                      {/* Customer */}
                      <td className="py-2.5 px-3 font-medium text-foreground">
                        <div>{o.customerName || "Customer"}</div>
                        <div className="text-[10px] text-muted-foreground font-price">
                          {o.phone}
                        </div>
                      </td>

                      {/* State */}
                      <td className="py-2.5 px-3 text-muted-foreground">
                        <div className="flex items-center gap-1 font-medium text-foreground flex-wrap">
                          <span>{o.state || "West Bengal"}</span>
                          {isWestBengalState(o.state || "West Bengal") ? (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 font-bold">
                              WB • CGST+SGST
                            </span>
                          ) : (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-700 dark:text-blue-300 font-bold">
                              IGST
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          {o.city} {o.pincode ? `(${o.pincode})` : ""}
                        </div>
                      </td>

                      {/* Items */}
                      <td className="py-2.5 px-3 max-w-[180px] truncate text-muted-foreground">
                        {(o.items || [])
                          .map((it) => `${it.quantity || 1}x ${it.name}`)
                          .join(", ") || "—"}
                      </td>

                      {/* Taxable */}
                      <td className="py-2.5 px-3 text-right font-price text-muted-foreground">
                        ₹{breakdown.taxableValue.toFixed(2)}
                      </td>

                      {/* Tax Amt */}
                      {hasGST && (
                        <td className="py-2.5 px-3 text-right font-price font-medium text-neutral-800 dark:text-neutral-200">
                          ₹{breakdown.totalTaxAmount.toFixed(2)}
                        </td>
                      )}

                      {/* Grand Total */}
                      <td className="py-2.5 px-3 text-right font-price font-bold text-foreground">
                        ₹{breakdown.grandTotal}
                      </td>

                      {/* Status */}
                      <td className="py-2.5 px-3">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-muted text-muted-foreground border border-border">
                          {o.status || "Completed"}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="py-2.5 px-3 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1.5">
                          <Link
                            to={`/admin/print/${o.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition"
                            title="View / Print Invoice"
                          >
                            <Printer className="w-3.5 h-3.5" />
                          </Link>

                          <button
                            data-testid={`dl-single-pdf-${o.id}`}
                            onClick={() => handleDownloadSinglePdf(o)}
                            disabled={singlePdfLoadingId === o.id || isExportingZip}
                            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition disabled:opacity-40"
                            title="Download Invoice as PDF"
                          >
                            {singlePdfLoadingId === o.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin text-terracotta" />
                            ) : (
                              <Download className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── Off-Screen Capture Stage for High-Res PDF Rendering ─── */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          left: "-99999px",
          top: 0,
          width: "210mm",
          pointerEvents: "none",
          zIndex: -999,
          opacity: 1,
        }}
      >
        {activeCaptureOrder && (
          <div ref={captureRef} style={{ width: "210mm", background: "#ffffff" }}>
            <InvoiceSheet
              order={activeCaptureOrder}
              products={products}
              company={COMPANY}
            />
          </div>
        )}
      </div>
    </div>
  );
}
